// Background upload worker.
// - Reads pending items from IndexedDB.
// - Mints a resumable Drive session (via callable), PUTs bytes directly to Google.
// - Resumes partial sessions using Content-Range probe (HARD RULE 7).
// - Uploads the thumbnail to Cloud Storage.
// - Calls finalizeUpload to flip the Firestore doc to `complete`.
// - Exponential backoff, tolerant of page-close.

import { getPending, updateItem, subscribe, removeItem, QueueItem } from './queue';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { functions, storage, auth } from './firebase';
import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { fire } from './analytics';

const CHUNK = 8 * 1024 * 1024; // 8MB PUT chunks — friendly on flaky venue wifi.

let running = false;
let onlineHandlerAttached = false;

export function startUploadWorker(): void {
  if (onlineHandlerAttached) return;
  onlineHandlerAttached = true;
  subscribe(() => void tick());
  window.addEventListener('online', () => void tick());
  window.addEventListener('focus', () => void tick());
  void tick();
}

// Errors that will never resolve themselves — no point retrying 8 times.
function isPermanentFailure(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  if (/appCheck|app-check|unauthenticated|permission-denied|invalid-argument/i.test(msg)) {
    return true;
  }
  return false;
}

async function tick() {
  if (running) return;
  if (!navigator.onLine) return;
  running = true;
  try {
    while (true) {
      const pending = await getPending();
      if (pending.length === 0) break;
      const item = pending[0];
      try {
        await processOne(item);
      } catch (err) {
        const attempts = item.attempts + 1;
        const backoffMs = Math.min(60_000, 1000 * 2 ** attempts);
        const failed = attempts >= 4 || isPermanentFailure(err);
        await updateItem(item.id, {
          status: failed ? 'failed' : 'pending',
          attempts,
          lastError: err instanceof Error ? err.message : String(err),
        });
        if (failed) {
          fire('upload_failed', {
            eventId: item.eventId,
            tableId: item.tableId ?? '',
            reason: err instanceof Error ? err.message.slice(0, 100) : 'unknown',
          });
          // Move on to the next item; leave this one in "failed" for the UI.
          continue;
        }
        // Wait before the next iteration.
        await sleep(backoffMs);
      }
    }
  } finally {
    running = false;
  }
}

async function processOne(item: QueueItem) {
  await updateItem(item.id, { status: 'uploading' });
  fire('upload_started', { eventId: item.eventId, tableId: item.tableId ?? '' });

  // 1) Write the Firestore upload doc first (status queued) so the couple
  //    dashboard can see the *attempt* even before bytes arrive.
  if (!item.sessionUri) {
    if (!auth.currentUser) {
      // Guest anon-signin should have already happened; skip until it does.
      throw new Error('no auth yet');
    }
    await setDoc(doc(db, `events/${item.eventId}/uploads/${item.id}`), {
      guestUid: auth.currentUser.uid,
      tableId: item.tableId ?? null,
      driveFileId: null,
      thumbPath: null,
      status: 'queued',
      uploaderName: item.uploaderName ?? null,
      createdAt: item.createdAt,
      hiddenAt: null,
      mimeType: item.mimeType,
      size: item.size,
    });

    const mint = httpsCallable<
      { slug: string; filename: string; mimeType: string; size: number; tableId?: string; origin: string },
      { sessionUri: string; eventId: string; slot: number }
    >(functions, 'mintUploadUrl');
    const { data } = await mint({
      slug: item.slug,
      filename: item.filename,
      mimeType: item.mimeType,
      size: item.size,
      tableId: item.tableId,
      origin: window.location.origin,
    });
    await updateItem(item.id, { sessionUri: data.sessionUri, driveSlot: data.slot });
    item.sessionUri = data.sessionUri;
    item.driveSlot = data.slot;
  }

  // 2) Resume probe: ask Drive how many bytes it already has.
  let start = item.bytesSent ?? 0;
  if (start === 0 && item.sessionUri) {
    start = await probeResumeOffset(item.sessionUri, item.size);
    if (start > 0) await updateItem(item.id, { bytesSent: start });
  }

  // 3) PUT chunks until done.
  let driveFileId = item.driveFileId;
  while (start < item.size) {
    const end = Math.min(start + CHUNK, item.size);
    const chunk = item.blob.slice(start, end);
    // Never set Content-Length manually — Safari/iOS forbids it, and the
    // browser derives it from the body anyway.
    const res = await fetch(item.sessionUri!, {
      method: 'PUT',
      headers: {
        'Content-Range': `bytes ${start}-${end - 1}/${item.size}`,
      },
      body: chunk,
    });

    if (res.status === 308) {
      // "Resume Incomplete" — parse Range header to learn how much was received.
      const range = res.headers.get('range');
      const nextStart = range ? parseInt(range.split('-')[1], 10) + 1 : end;
      start = nextStart;
      await updateItem(item.id, { bytesSent: start });
      continue;
    }
    if (res.status === 200 || res.status === 201) {
      const body = await res.json().catch(() => ({}));
      driveFileId = body.id ?? driveFileId;
      start = item.size;
      break;
    }
    // Anything else: fail this attempt (worker will retry).
    const bodyText = await res.text().catch(() => '');
    throw new Error(`Drive PUT ${res.status}: ${bodyText.slice(0, 200)}`);
  }

  if (!driveFileId) throw new Error('Drive upload finished without a file id');

  // 4) Upload the thumbnail.
  const thumbPath = `thumbs/${item.eventId}/${item.id}.jpg`;
  await uploadBytes(ref(storage, thumbPath), item.thumbBlob, { contentType: 'image/jpeg' });

  // 5) Finalize — server flips Firestore to `complete` and increments count.
  const finalize = httpsCallable<
    { eventId: string; uploadId: string; driveFileId: string; thumbPath: string; uploaderName?: string; tableId?: string; driveSlot?: number },
    { ok: true }
  >(functions, 'finalizeUpload');
  await finalize({
    eventId: item.eventId,
    uploadId: item.id,
    driveFileId,
    thumbPath,
    uploaderName: item.uploaderName,
    tableId: item.tableId,
    driveSlot: item.driveSlot,
  });

  await updateItem(item.id, { status: 'complete', driveFileId, thumbPath, bytesSent: item.size });
  fire('upload_complete', { eventId: item.eventId, tableId: item.tableId ?? '' });
  // Keep completed items around for a moment so the UI can show them, then GC.
  setTimeout(() => void removeItem(item.id), 60_000);
}

async function probeResumeOffset(sessionUri: string, totalSize: number): Promise<number> {
  try {
    // Content-Length is forbidden on Safari; body is empty so browser sets 0.
    const res = await fetch(sessionUri, {
      method: 'PUT',
      headers: { 'Content-Range': `bytes */${totalSize}` },
    });
    if (res.status === 308) {
      const range = res.headers.get('range');
      if (!range) return 0;
      return parseInt(range.split('-')[1], 10) + 1;
    }
    if (res.status === 200 || res.status === 201) return totalSize;
    return 0;
  } catch {
    return 0;
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
